import { defineComponent as _defineComponent } from 'vue'
import { Portal } from '@linusborg/vue-simple-portal';
import {
  ComponentPublicInstance,
  computed,
  ComputedRef,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  Ref,
  ref,
} from 'vue';

import AppIdent from '@/components/AppIdent.vue';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import LoadingAnimation from '@/components/LoadingAnimation.vue';
import ScrollableContent, { ScrollableContentApi } from '@/components/ScrollableContent.vue';
import BrowserDetection from '@/data/BrowserDetection';
import Tenant from '@/data/datatypes/Tenant';
import { trapFocus } from '@/data/helpers/DomHelper';
import { AppIdentDetails, MiniApp } from '@/data/tasks/MiniApp';
import { Routes } from '@/router';

// The duration in ms to wait before emitting the close event to allow the dialog to fade away
interface Props {
  id?: string;
  showCloseButton?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  showBackButton?: boolean;
  width?: string;
  height?: string;
  minHeight?: string;
  headerClasses?: string;
  scrollable?: boolean;
  outsideClickClosable?: boolean;
  fade?: boolean;
  inProgress?: boolean;
  disablePortal?: boolean;
  unpaddedContent?: boolean;
  // Props required when showing the app ident:
  app?: MiniApp | AppIdentDetails;
  userTenantId?: string;
  tenants?: Record<string, Tenant>;
  emitOnlyOnClose?: boolean; // If this is true, clicking close will only emit the close event, not close the dialog
}
interface Emits {
  (e: 'open'): void,
  (e: 'close', event?: Event): void,
  (e: 'edit', event?: Event): void,
  (e: 'delete', event?: Event): void,
}
export interface ModalDialogApi extends ComponentPublicInstance {
  closeDialog: (event?: Event) => Event | undefined;
  scrollToBottom: () => void;
  removeFromRouteModalCleanups: () => void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ModalDialog',
  props: {
    id: null,
    showCloseButton: { type: Boolean, default: false },
    showEditButton: { type: Boolean, default: false },
    showDeleteButton: { type: Boolean, default: false },
    showBackButton: { type: Boolean, default: false },
    width: { default: '40rem' },
    height: { default: '20rem' },
    minHeight: null,
    headerClasses: { default: '' },
    scrollable: { type: Boolean, default: true },
    outsideClickClosable: { type: Boolean, default: true },
    fade: { type: Boolean, default: true },
    inProgress: { type: Boolean, default: false },
    disablePortal: { type: Boolean, default: false },
    unpaddedContent: { type: Boolean, default: false },
    app: null,
    userTenantId: null,
    tenants: null,
    emitOnlyOnClose: { type: Boolean, default: false }
  },
  emits: ["open", "close", "edit", "delete"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'open'): void,
  (e: 'close', event?: Event): void,
  (e: 'edit', event?: Event): void,
  (e: 'delete', event?: Event): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { id?: string, showCloseButton: boolean, showEditButton: boolean, showDeleteButton: boolean, showBackButton: boolean, width: string, height: string, minHeight?: string, headerClasses: string, scrollable: boolean, outsideClickClosable: boolean, fade: boolean, inProgress: boolean, disablePortal: boolean, unpaddedContent: boolean, app?: MiniApp | AppIdentDetails, userTenantId?: string, tenants?: Record<string, Tenant>, emitOnlyOnClose: boolean }

const fadeDuration = 500;





const dialog: Ref<HTMLElement | null> = ref(null);
const scrollableContent: Ref<ScrollableContentApi | null> = ref(null);

const mouseDownOnBackground: Ref<boolean> = ref(false);
const show: Ref<boolean> = ref(false);

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const isSafariIos: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isSafariIos() || BrowserDetection.isIosApp();
});

const modalStyle = computed(() => {
  return {
    width: props.width,
    height: props.height,
    minHeight: props.minHeight,
  };
});

function emitClose(event?: Event): Event | undefined {
  emit('close', event);
  return event;
}

function edit(event?: Event): Event | undefined {
  emit('edit', event);
  return event;
}

function deleteEntry(event?: Event): Event | undefined {
  emit('delete', event);
  return event;
}

function handleMouseDown(event: Event): void {
  // We handle mousedown and mouseup manually due to a Vue bug where doing a mousedown on the dialog itself
  // and then mouseup on the background (e.g. selecting some text in the dialog, where you happen to drag
  // the mouse off the edge of the dialog) would incorrectly trigger the background's click handler.
  if (dialog.value === event.target) {
    mouseDownOnBackground.value = true;
  }
}

function handleMouseUp(event: MouseEvent): void {
  if (props.outsideClickClosable && mouseDownOnBackground.value && dialog.value === event.target) {
    closeDialog(event);
  }
  mouseDownOnBackground.value = false;
}

function onkey(event: KeyboardEvent) {
  if (event.key === 'Escape' && Routes.isTopModal(closeDialog)) {
    event.stopPropagation();
    closeDialog(event);
  }

  if (event.key === 'Tab' && Routes.isTopModal(closeDialog)) {
    event.stopPropagation();
    trapFocus(dialog.value, event);
  }
}

function scrollToBottom(): void {
  if (scrollableContent.value) {
    scrollableContent.value?.scrollToBottom();
  }
}

function closeDialog(event?: Event): Event | undefined {
  if (props.emitOnlyOnClose) {
    emit('close');
    return;
  }
  show.value = false;
  setTimeout(() => {
    emitClose(event);
  }, props.fade ? fadeDuration : 0);
  return event;
}

function removeFromRouteModalCleanups(): void {
  Routes.popModal(closeDialog);
}

onBeforeMount(() => {
  Routes.pushModal(closeDialog);
});

onBeforeUnmount(() => {
  Routes.popModal(closeDialog);
  window.removeEventListener('keydown', onkey);
});

window.addEventListener('keydown', onkey);
nextTick(() => {
  show.value = true;
  setTimeout(() => {
    emit('open');
  }, props.fade ? fadeDuration : 0);
});

expose({
  closeDialog,
  scrollToBottom,
  removeFromRouteModalCleanups,
});

return { __sfc: true,fadeDuration, props, emit, dialog, scrollableContent, mouseDownOnBackground, show, isMobile, isSafariIos, modalStyle, emitClose, edit, deleteEntry, handleMouseDown, handleMouseUp, onkey, scrollToBottom, closeDialog, removeFromRouteModalCleanups, Portal, AppIdent, BrandIcon, LoadingAnimation, ScrollableContent }
}

})